/**
 * Action buttons rendered inside an AI message bubble when the AI
 * calls the suggestVoiceActions tool. Shows "Add task", "Add event",
 * and a dismiss (X) button. Buttons grey out after use.
 */

import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { Spacing, BorderRadius } from '@/constants/spacing';
import type { VoiceActionItem } from '@/types/voice-transcription';

interface ChatActionButtonsProps {
  items: VoiceActionItem[];
  accentColor: string;
  onAddTask: (item: VoiceActionItem) => void;
  onAddEvent: (item: VoiceActionItem) => void;
  onDismiss: () => void;
  borderColor: string;
  /** When the suggestions came from an image, the image type surfaces as a label. */
  imageType?: 'flyer' | 'list' | 'calendar' | 'invitation' | 'receipt' | 'other';
  /** Worst confidence across items — used to indicate a low-confidence extraction. */
  worstConfidence?: 'high' | 'medium' | 'low';
}

export function ChatActionButtons({
  items,
  accentColor,
  onAddTask,
  onAddEvent,
  onDismiss,
  borderColor,
  imageType,
  worstConfidence,
}: ChatActionButtonsProps) {
  const [dismissed, setDismissed] = useState(false);
  const [taskAdded, setTaskAdded] = useState(false);
  const [eventAdded, setEventAdded] = useState(false);

  if (dismissed) return null;

  const taskItem = items.find((i) => i.type === 'task');
  const eventItem = items.find((i) => i.type === 'event');
  const isSubtaskUpdate = !!taskItem?.existingTaskId;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss();
  };

  const handleAddTask = (item: VoiceActionItem) => {
    if (taskAdded) return;
    setTaskAdded(true);
    onAddTask(item);
  };

  const handleAddEvent = (item: VoiceActionItem) => {
    if (eventAdded) return;
    setEventAdded(true);
    onAddEvent(item);
  };

  return (
    <Animated.View
      entering={FadeIn.duration(300)}
      exiting={FadeOut.duration(200)}
      style={[styles.container, { borderTopColor: borderColor }]}
    >
      {(imageType || worstConfidence) && (
        <View style={styles.badgeRow}>
          {imageType && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{imageType}</Text>
            </View>
          )}
          {worstConfidence && worstConfidence !== 'high' && (
            <View style={[styles.badge, styles.badgeWarn]}>
              <Text style={[styles.badgeText, styles.badgeWarnText]}>
                {worstConfidence} confidence
              </Text>
            </View>
          )}
        </View>
      )}
      <View style={styles.buttonsRow}>
        {taskItem && (
          <TouchableOpacity
            style={[styles.actionPill, { backgroundColor: taskAdded ? '#888888' : accentColor }]}
            onPress={() => handleAddTask(taskItem)}
            activeOpacity={0.7}
            disabled={taskAdded}
            accessibilityLabel="Add task"
            accessibilityRole="button"
          >
            <Ionicons name={taskAdded ? 'checkmark' : 'checkmark-circle-outline'} size={14} color="#FFFFFF" />
            <Text style={styles.pillText}>
              {taskAdded ? 'Adding...' : isSubtaskUpdate ? 'Add subtasks' : 'Add task'}
            </Text>
          </TouchableOpacity>
        )}

        {eventItem && (
          <TouchableOpacity
            style={[styles.actionPill, { backgroundColor: eventAdded ? '#888888' : accentColor }]}
            onPress={() => handleAddEvent(eventItem)}
            activeOpacity={0.7}
            disabled={eventAdded}
            accessibilityLabel="Add event"
            accessibilityRole="button"
          >
            <Ionicons name={eventAdded ? 'checkmark' : 'calendar-outline'} size={14} color="#FFFFFF" />
            <Text style={styles.pillText}>{eventAdded ? 'Adding...' : 'Add event'}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.dismissButton}
          onPress={handleDismiss}
          activeOpacity={0.7}
          accessibilityLabel="Dismiss suggestions"
          accessibilityRole="button"
        >
          <Ionicons name="close" size={14} color="#888888" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    marginTop: Spacing.sm,
    paddingTop: Spacing.sm,
  },
  buttonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  actionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: BorderRadius.xl,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  pillText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
  dismissButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(136, 136, 136, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeRow: {
    flexDirection: 'row',
    gap: Spacing.xs,
    marginBottom: Spacing.xs,
    flexWrap: 'wrap',
  },
  badge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    borderRadius: BorderRadius.xl,
    backgroundColor: 'rgba(0,0,0,0.08)',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#555',
    textTransform: 'capitalize',
  },
  badgeWarn: {
    backgroundColor: 'rgba(241, 91, 181, 0.18)',
  },
  badgeWarnText: {
    color: '#C72C7E',
  },
});
